import React, {useState, useEffect, useRef} from 'react'
import {Alert, Button, SortableList, indexId, IconButton, Modal, ModalActions, Popup, ProgressBar} from '../UI/index.js'
import {formSpacing, FormStack, Input, Select} from '../UI/forms/index.js'
import guid from '../../../utils/guid.js'
import {waitForPaint} from '../../utils/csvExport.js'
import t from '../../helpers/Languages.js'

/**
 * A CSV export with templates: the standard columns, or a saved template of the reseller with its own columns in its
 * own order. Templates are made, copied, saved and removed here, onSaveTemplates stores them.
 *
 * Props:
 * - title, open, onClose
 * - templates: the saved templates ({id, name, method, columns})
 * - onSaveTemplates(templates): stores the changed list
 * - methods: [{value, title}], what a line of the export is (per rit, per adres, ...). Only shown with more than one
 * - getColumns(method): the columns to choose from, {value, label, typeLabel?, typeColor?, typeTextColor?, info?}
 * - defaultColumns: the columns of the standard export, newTemplate: {name, columns, method} of a new one
 * - onClickExport(template, onProgress): exports with the chosen template
 */
export default function ExportTemplateModal(props) {
    const {title, open, onClose, templates, onSaveTemplates, methods, getColumns, defaultColumns, newTemplate, onClickExport} = props

    const [exportTemplate, setExportTemplate] = useState('default')
    const [activeTemplate, setActiveTemplate] = useState({
        name: '',
        method: methods[0].value
    })
    const [error, setError] = useState('')
    const [message, setMessage] = useState('')
    const [isCopying, setIsCopying] = useState(false)
    const [nameIsInvalid, setNameIsInvalid] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [progressNow, setProgressNow] = useState(0)
    const [progressMax, setProgressMax] = useState(0)
    const popupRef = useRef()
    const modalRef = useRef()
    const lastSelectRef = useRef()

    useEffect(() => {
        setNameIsInvalid(false)
        setError('')
        setMessage('')

        if (exportTemplate === 'new' && !isCopying) {
            setActiveTemplate(newTemplate)
        } else if (exportTemplate === 'default') {
            setActiveTemplate((prev) => ({
                ...prev,
                columns: defaultColumns,
                name: t('exportTemplateDefault')
            }))
        } else {
            setActiveTemplate((prev) => ({
                ...prev,
                ...templates.find((template) => template.id === exportTemplate)
            }))
        }
    }, [exportTemplate])

    useEffect(() => {
        if (!open) {
            setError('')
            setMessage('')
        }
    }, [open])

    const saveTemplate = () => {
        // Another template with the name, saving a template under its own name overwrites it
        const nameExists = templates.find((template) => template.name === activeTemplate.name && template.id !== activeTemplate.id)

        if (nameExists) {
            setError(t('exportTemplateNameExists', {name: nameExists.name}))
            return
        }

        if (popupRef.current) {
            const popupText = activeTemplate.id ? t('exportTemplateOverwriteText') : t('exportTemplateAddText')

            popupRef.current.open(t('exportTemplateSave'), popupText, () => {
                popupRef.current.close()

                if (!activeTemplate.id) {
                    activeTemplate.id = guid.generate()
                }

                const newTemplates = [...templates]
                const existingIndex = newTemplates.findIndex((template) => template.id === activeTemplate.id)

                if (existingIndex > -1) {
                    newTemplates[existingIndex] = activeTemplate
                } else {
                    newTemplates.push(activeTemplate)
                }

                setExportTemplate(activeTemplate.id)

                onSaveTemplates(newTemplates)

                setTimeout(() => {
                    setMessage(t('exportTemplateSaved', {name: activeTemplate.name}))
                }, 500)
            })
        }
    }

    const onRemoveTemplate = () => {
        if (popupRef.current) {
            popupRef.current.open(t('exportTemplateRemove'), t('exportTemplateRemoveText', {name: activeTemplate?.name}), () => {
                popupRef.current.close()

                const existingIndex = templates.findIndex((template) => template.id === activeTemplate.id)

                if (activeTemplate.id && existingIndex > -1) {
                    const name = templates[existingIndex].name

                    setExportTemplate('new')

                    onSaveTemplates(templates.filter((template) => template.id !== activeTemplate.id))

                    setMessage(t('exportTemplateRemoved', {name}))
                } else {
                    setError(t('exportTemplateRemoveFailed'))
                }
            })
        }
    }

    const onCopy = () => {
        setIsCopying(true)

        setExportTemplate('new')

        setActiveTemplate((prev) => ({
            ...prev,
            id: undefined,
            name: ''
        }))

        setNameIsInvalid(true)
        modalRef.current?.scrollTo(0, 0)

        setTimeout(() => {
            setIsCopying(false)
        }, 1000)
    }

    const onExport = async () => {
        setExporting(true)
        setProgressNow(0)
        setProgressMax(0)

        try {
            // Let the loading state render before the parent does synchronous work
            await waitForPaint()

            await onClickExport(activeTemplate, (now, max) => {
                setProgressNow(now)
                setProgressMax(max)
            })
        } catch (err) {
            setError(err.message)
        } finally {
            setExporting(false)
        }
    }

    return (
        <Modal
            show={open}
            closeButton
            onClose={onClose}
            title={title}
            ref={modalRef}
        >
            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                <Select
                    style={{marginBottom: 12}}
                    value={exportTemplate}
                    onChange={(event) => setExportTemplate(event.target.value)}
                    options={[
                        {
                            value: 'new',
                            title: t('exportTemplateNew')
                        },
                        {
                            value: 'default',
                            title: t('exportTemplateDefault')
                        },
                        ...(templates.map((template) => ({
                            value: template.id,
                            title: template.name
                        })))
                    ]}
                    label={t('exportTemplate')}
                />

                {methods.length > 1 &&
                    <Select
                        value={activeTemplate.method}
                        onChange={(event) => {
                            const value = event.target.value
                            setActiveTemplate((prev) => ({...prev, method: value}))
                        }}
                        options={methods}
                        label={t('exportMethod')}
                    />
                }
            </FormStack>

            {activeTemplate && (
                <div style={{marginTop: 24, marginBottom: 24}}>
                    <Input
                        value={activeTemplate.name}
                        onChange={(event) => {
                            const value = event.target.value // https://stackoverflow.com/questions/44708518/event-target-is-null-inside-functional-setstate
                            setActiveTemplate((prev) => ({...prev, name: value}))
                        }}
                        label={t('name')}
                        style={{marginBottom: 24}}
                        isInvalid={nameIsInvalid}
                    />

                    <SortableList
                        dragHandle
                        getId={indexId}
                        // Block, like the list before: the items' bottom margins collapse with the spacing around it
                        style={{display: 'block'}}
                        items={activeTemplate.columns}
                        renderItem={(column, DragHandle, index) => {
                            return (
                                <div style={{display: 'flex', alignItems: 'baseline', gap: formSpacing.tight, marginBottom: 12}}>
                                    <p style={{width: 20}}>{index + 1}</p>
                                    <DragHandle />

                                    <Select
                                        value={column || ''}
                                        options={getColumns(activeTemplate.method).map((column) => ({
                                            value: column.value || '',
                                            title: column.label,
                                            badge: column.typeLabel,
                                            badgeColor: column.typeColor,
                                            badgeTextColor: column.typeTextColor || '',
                                            info: column.info || ''
                                        }))}
                                        onChange={(event) => {
                                            const newColumns = structuredClone(activeTemplate.columns)
                                            newColumns[index] = event.target.value
                                            setActiveTemplate((prev) => ({
                                                ...prev,
                                                columns: newColumns
                                            }))
                                        }}
                                        ref={index === activeTemplate.columns.length - 1 ? lastSelectRef : undefined}
                                    />
                                    <IconButton
                                        icon='mdi mdi-delete'
                                        onClick={() => {
                                            const newColumns = structuredClone(activeTemplate.columns)
                                            newColumns.splice(index, 1)
                                            setActiveTemplate((prev) => ({
                                                ...prev,
                                                columns: newColumns
                                            }))
                                        }}
                                    />
                                </div>
                            )
                        }}
                        onChange={(columns) => {
                            setActiveTemplate((prev) => ({
                                ...prev,
                                columns
                            }))
                        }}
                    />
                    <div style={{display: 'flex', columnGap: formSpacing.columnGap, marginTop: 12}}>
                        <Button
                            icon='mdi mdi-plus'
                            variant='outline-white'
                            label={t('exportTemplateAddColumn')}
                            onClick={() => {
                                setActiveTemplate((prev) => ({
                                    ...prev,
                                    columns: [...activeTemplate.columns, '']
                                }))
                                setTimeout(() => {
                                    lastSelectRef?.current?.focus()
                                }, 100)
                            }}
                        />

                        <Button
                            variant='outline-white'
                            label={t('save')}
                            onClick={saveTemplate}
                            disabled={!activeTemplate.name}
                        />

                        <Button
                            variant='outline-white'
                            label={t('copy')}
                            onClick={onCopy}
                        />

                        {exportTemplate !== 'new' &&
                            <Button
                                variant='outline-white'
                                label={t('delete')}
                                onClick={onRemoveTemplate}
                            />
                        }
                    </div>
                </div>
            )}

            {error && <Alert style={{marginBottom: 6}} variant='error' onClose={() => setError('')}>{error}</Alert>}

            {message && <Alert style={{marginBottom: 6}} variant='success' onClose={() => setMessage('')}>{message}</Alert>}

            {exporting && progressMax > 0 &&
                <ProgressBar style={{marginBottom: 16}} now={progressNow} max={progressMax} />
            }

            <ModalActions>
                <Button onClick={onClose} variant='text' disabled={exporting}>{t('cancel')}</Button>
                <Button onClick={onExport} loading={exporting}>{t('export')}</Button>
            </ModalActions>

            <Popup
                ref={popupRef}
            />
        </Modal>
    )
}
